import { useMutation } from "@apollo/client";
import { Membership, MembershipBillingPlan } from "@app/shared/types";
import { Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { GRAPHQL_MUTATION_MEMBERSHIP_BILLING } from "app/queries";
import { routes } from "app/routes";
import { showSnackbarAlertOnRedirect } from "components/AlertSnackBar";
import { push } from "connected-react-router";
import { LoadingPage } from "features/pages/LoadingPage";
import { SignupBillingPricePage } from "features/signup/SignupBillingPricePage";
import { useState } from "react";
import { useDispatch } from "react-redux";

const useStyles = makeStyles((theme) => ({
    root: {
        //textAlign: "center",
        padding: theme.spacing(4),
        marginTop: theme.spacing(4),
        [theme.breakpoints.up("md")]: {
            display: "flex",
        },
    },
    submitButtonArea: {
        marginTop: "4rem",
        marginBottom: "10rem",
        [theme.breakpoints.up("md")]: {
            width: "35%",
            paddingLeft: theme.spacing(4),
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
        },
    },
    cancelButton: {
        marginTop: theme.spacing(3),
    },
    contentArea: {
        [theme.breakpoints.up("md")]: {
            width: "65%",
        },
    },
}));

export const getBillingPlansToShow = (
    membership: Membership,
    currentPlanName: string | undefined,
) => {
    return membership.slidingScaleBillingPlanOptions.filter((plan) => plan.available);
};

export const getInitialPlan = (
    billingPlansToShow: MembershipBillingPlan[],
    currentPlanName: string | undefined,
) => {
    const initialPlan = billingPlansToShow.find((choice) => choice.planName === currentPlanName);

    // If the member is on a deprecated sliding scale price, it may not be in the billingPlans shown. In this case, default to the base price
    return initialPlan || billingPlansToShow[Math.floor((billingPlansToShow.length - 1) / 2)];
};

export const UpdateBillingForm = (props: { membership: Membership }) => {
    const dispatch = useDispatch();

    const { membership } = props;

    const [updateMembershipMutation, { loading: isSaving }] = useMutation(
        GRAPHQL_MUTATION_MEMBERSHIP_BILLING,
        {
            refetchQueries: ["Membership", "MyMemberships"],
        },
    );

    const billingChoice = membership.scheduledChange?.billingChoice || membership.billingChoice;
    const currentPlanName = billingChoice?.plan.planName;

    const billingPlans = getBillingPlansToShow(membership, currentPlanName);
    const initialPlan = getInitialPlan(billingPlans, currentPlanName);

    const [selectedPlan, setSelectedPlan] = useState(initialPlan);

    const [donationAmount, setDonationAmount] = useState(
        membership.billingChoice ? membership.billingChoice.donationAmount : 10,
    );

    const onSelectPlan = (plan: MembershipBillingPlan) => {
        setSelectedPlan(plan);
    };

    const onSubmit = async (overrideDonationAmount = donationAmount) => {
        await updateMembershipMutation({
            variables: {
                membershipId: membership.id,
                billingChoice: {
                    planName: selectedPlan.planName,
                    donationAmount: overrideDonationAmount,
                },
            },
        });
        showSnackbarAlertOnRedirect("Your contribution has been updated");
        dispatch(push(routes.memberMembership()));
    };

    let currentAmount = billingChoice?.plan.baseAmount;
    // Special case needed for legacy discounted MMTCP members who were billed semi-annually
    if (currentAmount && billingChoice?.plan.billedEvery === "6 months") {
        currentAmount /= 6;
    }

    const billingNote = membership.billingChoice.plan.slidingScale
        ? "Change will be reflected on your next bill."
        : "Change will be effective immediately, and will be credited from what you've already paid";

    if (isSaving) {
        return <LoadingPage />;
    }

    return (
        <SignupBillingPricePage
            billingPlanOptions={billingPlans}
            billingPlan={selectedPlan}
            onChangeBillingPlan={onSelectPlan}
            onContinue={() => {
                // Override the donation amount to 0 for existing members who may have a donation, who are switching to sliding scale pricing
                onSubmit(0);
            }}
            note={<Typography variant="body2">{billingNote}</Typography>}
        />
    );
};

export default UpdateBillingForm;
